import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, AlertCircle, Upload, FileText, Trash2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import {
  acceptDe,
  etiquetasDe,
  formatBytes,
  limiteDe,
  validarArchivo,
  type TipoDocumento,
} from "@/lib/formatosDocumento";

type ArchivoEstado = "PENDIENTE" | "SUBIENDO" | "OK" | "ERROR";

interface ArchivoItem {
  file: File;
  estado: ArchivoEstado;
  error?: string;
}

/**
 * Modal genérico para subir documentos al kernel.documental y opcionalmente
 * vincularlos a un recurso del sector. Soporta carga múltiple (todos los
 * archivos seleccionados se suben como instancias separadas del mismo tipo).
 *
 * Modos:
 *   - Carga inicial: sin {@code documentoExistenteId} → POST /api/documentos (N veces).
 *     onUploaded se invoca una vez por cada documento creado.
 *   - Nueva versión: con {@code documentoExistenteId} → POST a /versiones,
 *     fuerza single-file (una versión es 1 archivo por definición).
 *   - Pre-selección: {@code tipoCodigoFijo} oculta el dropdown y fija el tipo.
 */
export function UploadDocumentoModal({
  open,
  title,
  contextoTipo,
  contextoId,
  tag,
  filterTipos,
  tipoCodigoFijo,
  documentoExistenteId,
  esCopiaCertificadaInicial,
  pedirCopiaCertificada,
  onClose,
  onUploaded,
}: {
  open: boolean;
  title: string;
  contextoTipo: string;
  contextoId: string;
  /** Filtro server-side por kernel.tipo_documento.tags (ej. "TELECOM", "REGISTRO_LEGAL"). */
  tag?: string;
  /** Filtro adicional client-side. Útil para refinar dentro del tag. */
  filterTipos?: (t: TipoDocumento) => boolean;
  /** Si viene, fija el tipo de documento y oculta el dropdown. */
  tipoCodigoFijo?: string;
  /** Si viene, sube nueva versión a este documento; fuerza single-file. */
  documentoExistenteId?: string;
  /** Pre-marca el checkbox de copia certificada original. */
  esCopiaCertificadaInicial?: boolean;
  /** Muestra el checkbox de copia certificada (default: solo si tipo lo requiere). */
  pedirCopiaCertificada?: boolean;
  onClose: () => void;
  /** Callback con el documentoId subido. Se invoca una vez por archivo. */
  onUploaded: (documentoId: string) => Promise<void>;
}) {
  const esNuevaVersion = !!documentoExistenteId;
  // Carga múltiple sólo cuando el flujo es "catálogo libre": el usuario elige
  // el tipo y puede subir N archivos del mismo tipo. Flujos guiados con
  // tipoCodigoFijo (CURP del RL, etc.) son semánticamente 1:1 → single.
  const permiteMultiple = !esNuevaVersion && !tipoCodigoFijo;
  const [tipoCodigo, setTipoCodigo] = useState(tipoCodigoFijo ?? "");
  const [archivos, setArchivos] = useState<ArchivoItem[]>([]);
  const [descripcion, setDescripcion] = useState("");
  const [esCopia, setEsCopia] = useState(esCopiaCertificadaInicial ?? false);
  const [loading, setLoading] = useState(false);
  const [progreso, setProgreso] = useState<{ actual: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const qTipos = useQuery({
    queryKey: ["documentos-tipos", tag ?? "all"],
    queryFn: async () =>
      (await api.get<TipoDocumento[]>(`/api/documentos/tipos${tag ? `?tag=${encodeURIComponent(tag)}` : ""}`)).data,
    enabled: open,
  });

  if (!open) return null;

  const tipos = qTipos.data ? (filterTipos ? qTipos.data.filter(filterTipos) : qTipos.data) : [];
  const tipoSeleccionado = tipos.find((t) => t.codigo === tipoCodigo);
  const mostrarCopia = pedirCopiaCertificada ?? !!tipoSeleccionado?.requiereCopiaCertificada;

  const reset = () => {
    setTipoCodigo(tipoCodigoFijo ?? "");
    setArchivos([]);
    setDescripcion("");
    setEsCopia(esCopiaCertificadaInicial ?? false);
    setError(null);
    setProgreso(null);
  };
  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const onSeleccionarArchivos = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const nuevos: ArchivoItem[] = Array.from(files).map((f) => ({ file: f, estado: "PENDIENTE" }));
    setArchivos((prev) => (permiteMultiple ? [...prev, ...nuevos] : nuevos.slice(0, 1)));
    setError(null);
  };

  const removerArchivo = (index: number) => {
    setArchivos((prev) => prev.filter((_, i) => i !== index));
  };

  const subirUno = async (item: ArchivoItem): Promise<{ ok: boolean; error?: string }> => {
    const form = new FormData();
    form.append("archivo", item.file);
    form.append("esCopiaCertificadaOriginal", String(esCopia));
    try {
      let resp;
      if (documentoExistenteId) {
        resp = await api.post<{ documentoId: string }>(
          `/api/documentos/${documentoExistenteId}/versiones`, form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
      } else {
        form.append("tipoDocumentoCodigo", tipoCodigo);
        form.append("contextoTipo", contextoTipo);
        form.append("contextoId", contextoId);
        if (descripcion.trim()) form.append("descripcion", descripcion.trim());
        resp = await api.post<{ documentoId: string }>("/api/documentos", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      await onUploaded(resp.data.documentoId);
      return { ok: true };
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string; title?: string } } }).response?.data?.detail ||
        (e as { response?: { data?: { title?: string } } }).response?.data?.title ||
        (e as Error).message ||
        "Error inesperado.";
      return { ok: false, error: detail };
    }
  };

  const submit = async () => {
    if (!tipoCodigo) {
      setError("Selecciona un tipo de documento.");
      return;
    }
    if (archivos.length === 0) {
      setError("Selecciona al menos un archivo.");
      return;
    }
    // Validación local previa al envío (el backend revalida contra el contenido real).
    const errLocal = archivos
      .map((a) => ({ a, err: validarArchivo(tipoSeleccionado, a.file) }))
      .find((x) => x.err);
    if (errLocal) {
      setError(`${errLocal.a.file.name}: ${errLocal.err}`);
      return;
    }
    setError(null);
    setLoading(true);

    const resultado: ArchivoItem[] = archivos.map((a) => ({ ...a, estado: "PENDIENTE" as ArchivoEstado }));
    for (let i = 0; i < resultado.length; i++) {
      resultado[i] = { ...resultado[i], estado: "SUBIENDO" };
      setArchivos([...resultado]);
      setProgreso({ actual: i + 1, total: resultado.length });
      const r = await subirUno(resultado[i]);
      resultado[i] = r.ok
        ? { ...resultado[i], estado: "OK" }
        : { ...resultado[i], estado: "ERROR", error: r.error };
      setArchivos([...resultado]);
    }

    setLoading(false);
    setProgreso(null);

    const fallidos = resultado.filter((x) => x.estado === "ERROR");
    if (fallidos.length === 0) {
      reset();
      onClose();
    } else {
      setError(
        `${resultado.length - fallidos.length} de ${resultado.length} subidos correctamente. ` +
          `Revisa los errores y reintenta los que faltan.`,
      );
      // Conservamos sólo los fallidos en la lista para que el usuario pueda reintentar.
      setArchivos(fallidos.map((x) => ({ ...x, estado: "PENDIENTE", error: x.error })));
    }
  };

  const tamanoMaxLabel = limiteDe(tipoSeleccionado);

  // La acción principal dice cuántos documentos se van a procesar: con la lista
  // desplazada fuera de vista, el contador es la única señal de cuántos son.
  const etiquetaAccion = loading
    ? archivos.length > 1
      ? `Subiendo ${archivos.length} documentos…`
      : "Subiendo documento…"
    : archivos.length > 1
      ? `Subir y vincular ${archivos.length} documentos`
      : "Subir y vincular documento";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      {/*
        Tres zonas en columna: encabezado y acciones fijos, cuerpo con scroll
        propio. Sin el max-h el modal crecía con la lista de archivos y las
        acciones quedaban fuera del viewport, así que con varios documentos
        seleccionados parecía que no había forma de subirlos.
      */}
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-lg bg-surface shadow-overlay">
        <div className="flex shrink-0 items-start justify-between border-b px-5 py-3">
          <h2 className="text-base font-semibold text-fg">{title}</h2>
          <button
            onClick={handleClose}
            className="cursor-pointer rounded p-1 text-fg-faint transition hover:bg-surface-2 hover:text-fg-muted"
            disabled={loading}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {!tipoCodigoFijo && (
            <div>
              <label className="text-xs uppercase tracking-wide text-fg-subtle">
                Tipo de documento <span className="text-estado-err">*</span>
              </label>
              <select
                value={tipoCodigo}
                onChange={(e) => setTipoCodigo(e.target.value)}
                className="mt-1 w-full rounded border border-edge-strong px-2 py-1 text-sm focus:border-primary focus:outline-none"
                disabled={loading || qTipos.isLoading}
              >
                <option value="">— Selecciona —</option>
                {tipos.map((t) => (
                  <option key={t.id} value={t.codigo}>
                    {t.nombre}
                    {t.requeridoTelecomBasico && " ★"}
                  </option>
                ))}
              </select>
            </div>
          )}
          {tipoSeleccionado && (
            <div className="rounded border border-edge bg-surface-2/60 p-2 text-xs text-fg-subtle">
              <span className="font-medium text-fg-muted">{tipoSeleccionado.nombre}</span>
              {tipoSeleccionado.descripcion ? ` — ${tipoSeleccionado.descripcion}` : ""}
              <div className="mt-1">
                Formatos permitidos: <span className="font-medium text-fg-muted">{etiquetasDe(tipoSeleccionado)}</span>
              </div>
              <div>
                Tamaño máximo: <span className="font-medium text-fg-muted">{tamanoMaxLabel}</span>
              </div>
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-wide text-fg-subtle">
              Archivo{permiteMultiple ? "s" : ""} <span className="text-estado-err">*</span>
              {permiteMultiple && (
                <span className="ml-2 normal-case tracking-normal text-[11px] text-fg-faint">
                  Puedes seleccionar varios a la vez
                </span>
              )}
            </label>
            <input
              type="file"
              multiple={permiteMultiple}
              onChange={(e) => {
                onSeleccionarArchivos(e.target.files);
                // Reset value para permitir re-seleccionar el mismo archivo si lo quitó.
                e.target.value = "";
              }}
              className="mt-1 block w-full text-sm file:mr-3 file:cursor-pointer file:rounded file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:text-white file:transition hover:file:bg-primary-hover"
              disabled={loading}
              accept={acceptDe(tipoSeleccionado)}
            />
          </div>

          {archivos.length > 0 && (
            <ul className="space-y-1 rounded border border-edge bg-surface-2 p-2 text-sm">
              {archivos.map((a, i) => (
                <li
                  key={`${a.file.name}-${i}`}
                  className="flex items-center justify-between gap-2 rounded bg-surface px-2 py-1.5 shadow-sm"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    {a.estado === "OK" ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-estado-ok" />
                    ) : a.estado === "ERROR" ? (
                      <AlertCircle className="h-4 w-4 shrink-0 text-estado-err" />
                    ) : (
                      <FileText className="h-4 w-4 shrink-0 text-fg-faint" />
                    )}
                    <div className="min-w-0">
                      <div className="truncate text-sm text-fg" title={a.file.name}>
                        {a.file.name}
                      </div>
                      <div className="text-[11px] text-fg-subtle">
                        {formatBytes(a.file.size)}
                        {a.estado === "SUBIENDO" && " · subiendo…"}
                        {a.estado === "OK" && " · subido"}
                        {a.estado === "ERROR" && a.error && (
                          <span className="text-estado-err"> · {a.error}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {!loading && a.estado !== "OK" && (
                    <button
                      type="button"
                      onClick={() => removerArchivo(i)}
                      className="cursor-pointer rounded p-1 text-fg-faint transition hover:bg-surface-2 hover:text-estado-err"
                      title="Quitar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {mostrarCopia && (
            <label className="flex items-start gap-2 rounded border border-estado-warn/25 bg-estado-warn-bg p-2 text-xs text-estado-warn">
              <input
                type="checkbox"
                checked={esCopia}
                onChange={(e) => setEsCopia(e.target.checked)}
                className="mt-0.5"
                disabled={loading}
              />
              <span>
                {archivos.length > 1 ? "Todos los archivos son copia certificada original." : "Es copia certificada original."}
                {esCopiaCertificadaInicial && " El requisito exige copia certificada para esta combinación."}
              </span>
            </label>
          )}

          {!documentoExistenteId && (
            <div>
              <label className="text-xs uppercase tracking-wide text-fg-subtle">
                Descripción (opcional)
                {archivos.length > 1 && (
                  <span className="ml-2 normal-case tracking-normal text-[11px] text-fg-faint">
                    Se aplicará a todos los archivos
                  </span>
                )}
              </label>
              <textarea
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                rows={2}
                className="mt-1 w-full rounded border border-edge-strong px-2 py-1 text-sm focus:border-primary focus:outline-none"
                disabled={loading}
              />
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded bg-estado-err-bg p-2 text-xs text-estado-err">
              <AlertCircle className="mt-0.5 h-4 w-4" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-2 border-t bg-surface-2 px-5 py-3">
          <div className="text-xs text-fg-subtle">
            {progreso && `Subiendo ${progreso.actual} de ${progreso.total}…`}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleClose}
              className="cursor-pointer rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              onClick={submit}
              className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover disabled:opacity-50"
              disabled={loading || !tipoCodigo || archivos.length === 0}
            >
              <Upload className="h-4 w-4" />
              {etiquetaAccion}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

